/**
 * Offline Debug Utility
 * Centralized debug logging for offline-first architecture
 * Set OFFLINE_DEBUG to true to enable detailed logging
 */

// Toggle this flag to enable/disable offline debugging
export const OFFLINE_DEBUG = true;

interface DebugContext {
  component: string;
  branchId?: string;
  categoryId?: string;
  isOnline?: boolean;
  isElectronApp?: boolean;
  hasDatabase?: boolean;
}

/**
 * Log offline-related debug information
 */
export function offlineLog(
  level: 'info' | 'warn' | 'error' | 'success',
  message: string,
  context?: DebugContext,
  data?: unknown
): void {
  if (!OFFLINE_DEBUG) return;

  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  const prefix = `[Offline:${timestamp}]`;
  const contextStr = context ? ` [${context.component}]` : '';
  
  const styles = {
    info: 'color: #2196F3',
    warn: 'color: #FF9800',
    error: 'color: #F44336',
    success: 'color: #4CAF50',
  };

  const icons = {
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
    success: '✅',
  };

  const fullMessage = `${prefix}${contextStr} ${icons[level]} ${message}`;

  if (data !== undefined) {
    console.log(`%c${fullMessage}`, styles[level], data);
  } else {
    console.log(`%c${fullMessage}`, styles[level]);
  }

  // Log context details on info level
  if (context && level === 'info') {
    console.log(`%c${prefix}${contextStr} Context:`, 'color: #9E9E9E', context);
  }
}

/**
 * Log cache write operation
 */
export function logCacheWrite(
  component: string,
  entityType: string,
  count: number,
  branchId?: string
): void {
  offlineLog('success', `Cache WRITE: ${count} ${entityType}`, { component, branchId });
}

/**
 * Log cache read operation
 */
export function logCacheRead(
  component: string,
  entityType: string,
  count: number,
  source: 'sqlite' | 'supabase' | 'indexeddb',
  branchId?: string
): void {
  offlineLog('info', `Cache READ: ${count} ${entityType} from ${source}`, { component, branchId });
}

/**
 * Log offline mode activation
 */
export function logOfflineMode(
  component: string,
  isOffline: boolean,
  context: Omit<DebugContext, 'component'>
): void {
  if (isOffline) {
    offlineLog('warn', 'Offline mode ACTIVATED - using local cache', { component, ...context });
  } else {
    offlineLog('info', 'Online mode - using Supabase', { component, ...context });
  }
}

/**
 * Log prefetch operation
 */
export function logPrefetch(
  status: 'start' | 'success' | 'error',
  branchId: string,
  details?: { categoriesCount?: number; itemsCount?: number; error?: unknown }
): void {
  const component = 'Prefetch';
  
  switch (status) {
    case 'start':
      offlineLog('info', `Starting menu prefetch for branch: ${branchId}`, { component, branchId });
      break;
    case 'success':
      offlineLog('success', `Prefetch complete: ${details?.categoriesCount || 0} categories, ${details?.itemsCount || 0} items`, { component, branchId });
      break;
    case 'error': {
      // Properly extract error message from various error types
      let errorMsg = 'Unknown error';
      const err = details?.error;
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err && typeof err === 'object') {
        const errObj = err as Record<string, unknown>;
        errorMsg = String(errObj.message || errObj.error || errObj.code || JSON.stringify(err));
      }
      offlineLog('error', `Prefetch failed: ${errorMsg}`, { component, branchId });
      break;
    }
  }
}

/**
 * Summary of offline capabilities
 */
export function logOfflineSummary(context: DebugContext): void {
  if (!OFFLINE_DEBUG) return;

  console.group(`%c[Offline Summary] ${context.component}`, 'color: #673AB7; font-weight: bold');
  console.log('Is Electron App:', context.isElectronApp);
  console.log('Is Online:', context.isOnline);
  console.log('Has Database:', context.hasDatabase);
  console.log('Branch ID:', context.branchId || 'Not set');
  console.log('Offline Ready:', context.isElectronApp && context.hasDatabase);
  console.groupEnd();
}
